/**
 * CLI handlers for `nexus plugin` commands.
 *
 * File-based plugin state (nexus.plugins.json). No DB dependency for plugin management.
 */

import { existsSync, readFileSync } from 'node:fs'
import { join } from 'node:path'
import Table from 'cli-table3'
import { consola } from 'consola'
import { OFFICIAL_PLUGINS } from '@gzl10/nexus-sdk'
import { discoverPlugins } from '@kernel/config/load-config.js'
import { getProjectPath } from '@kernel/config/paths.js'
import { initCli } from './shared.js'
import {
  shortPluginName,
  resolvePluginName,
  getAllPluginStates, getPluginState,
  isPackageInstalled,
  installPlugin, uninstallPlugin,
  enablePlugin, disablePlugin,
  type PluginEntry
} from '../core/plugin-ops.js'

/**
 * Derive a human-readable label from a plugin package name.
 * '@gzl10/nexus-plugin-auth-providers' → 'Auth Providers'
 */
function pluginLabel(name: string): string {
  return shortPluginName(name)
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * Read version from a plugin's package.json in node_modules.
 * Returns null if not found.
 */
function readPluginVersion(projectPath: string, pkgName: string): string | null {
  const pkgJsonPath = join(projectPath, 'node_modules', pkgName, 'package.json')
  if (!existsSync(pkgJsonPath)) return null
  try {
    const pkg = JSON.parse(readFileSync(pkgJsonPath, 'utf-8')) as { version?: string }
    return pkg.version ?? null
  } catch {
    return null
  }
}

// ── nexus plugin list ───────────────────────────────────────────────────────

export async function handlePluginList(options: { json?: boolean } = {}): Promise<void> {
  initCli()

  const projectPath = getProjectPath()
  const discovered = await discoverPlugins(projectPath)
  const states = getAllPluginStates(projectPath)

  if (options.json) {
    const result = discovered.map(p => ({
      name: shortPluginName(p.name),
      fullName: p.name,
      code: p.code,
      version: p.version,
      enabled: states[p.name]?.enabled ?? true
    }))
    // Include orphans (in state but not discovered)
    for (const [name, state] of Object.entries(states)) {
      if (!discovered.some(p => p.name === name)) {
        result.push({
          name: shortPluginName(name),
          fullName: name,
          code: '–',
          version: readPluginVersion(projectPath, name) ?? '–',
          enabled: state.enabled
        })
      }
    }
    console.log(JSON.stringify(result, null, 2))
    return
  }

  const installedNames = new Set(discovered.map(p => p.name))

  if (discovered.length > 0 || Object.keys(states).length > 0) {
    console.log('\n  Installed plugins:')
    const table = new Table({ head: ['Name', 'Code', 'Version', 'Enabled'] })

    for (const p of discovered) {
      const state = states[p.name]
      table.push([
        shortPluginName(p.name),
        p.code,
        p.version,
        state ? (state.enabled ? 'yes' : 'no') : '–',
      ])
    }

    // Orphans: in nexus.plugins.json but not discovered (e.g. factory plugins, missing build)
    for (const [name, state] of Object.entries(states)) {
      if (!discovered.some(p => p.name === name)) {
        installedNames.add(name)
        const version = readPluginVersion(projectPath, name)
        table.push([shortPluginName(name), '–', version ?? '–', state.enabled ? 'yes' : 'no'])
      }
    }

    console.log(table.toString())
  } else {
    consola.info('No plugins installed.')
  }

  const available = OFFICIAL_PLUGINS.filter(name => !installedNames.has(name))
  if (available.length > 0) {
    console.log('\n  Available (not installed):')
    const availTable = new Table({ head: ['Name', 'Package'] })
    for (const name of available) {
      availTable.push([pluginLabel(name), name])
    }
    console.log(availTable.toString())
    console.log(`\n  Install: nexus plugin add <name>`)
  }
}

// ── nexus plugin add <name> ─────────────────────────────────────────────────

export async function handlePluginAdd(name: string, options: { version?: string }): Promise<void> {
  if (process.env['NEXUS_ALLOW_PLUGIN_INSTALL'] === 'false') {
    consola.warn('Plugin installation is disabled (NEXUS_ALLOW_PLUGIN_INSTALL=false)')
    return
  }

  initCli()

  const projectPath = getProjectPath()
  const discovered = await discoverPlugins(projectPath)
  const fullName = resolvePluginName(name, discovered)

  try {
    consola.start(`Installing ${fullName}${options.version ? `@${options.version}` : ''}...`)
    await installPlugin(fullName, { version: options.version })
    consola.success(`Plugin ${fullName} installed and enabled.`)
  } catch (err) {
    consola.error(`Failed to install plugin: ${(err as Error).message}`)
  }
}

// ── nexus plugin remove <name> ──────────────────────────────────────────────

export async function handlePluginRemove(name: string | undefined, options: { all?: boolean } = {}): Promise<void> {
  if (process.env['NEXUS_ALLOW_PLUGIN_INSTALL'] === 'false') {
    consola.warn('Plugin management is disabled (NEXUS_ALLOW_PLUGIN_INSTALL=false)')
    return
  }

  if (!name && !options.all) {
    consola.warn('Provide a plugin name or use -a to remove all.')
    return
  }
  if (name && options.all) {
    consola.warn('Cannot combine plugin name with -a flag.')
    return
  }

  initCli()
  const projectPath = getProjectPath()
  const discovered = await discoverPlugins(projectPath)

  if (options.all) {
    const states = getAllPluginStates(projectPath)
    const names = Object.keys(states)

    if (names.length === 0) {
      consola.info('No plugins installed.')
      return
    }

    const table = new Table({ head: ['Name', 'Enabled'] })
    for (const n of names) {
      table.push([shortPluginName(n), states[n]?.enabled ? 'yes' : 'no'])
    }

    console.log('\n  Plugins to remove:')
    console.log(table.toString())
    console.log(`\n  Tip: run "nexus migrate drop" from each plugin directory first to drop their tables.`)

    const confirmed = await consola.prompt(
      `Remove ALL ${names.length} plugins? This cannot be undone.`,
      { type: 'confirm', initial: false }
    )

    if (!confirmed || typeof confirmed === 'symbol') {
      consola.info('Aborted.')
      return
    }

    for (const n of names) {
      try {
        consola.start(`Removing ${shortPluginName(n)}...`)
        await uninstallPlugin(n)
        consola.success(`${shortPluginName(n)} removed.`)
      } catch (err) {
        consola.error(`Failed to remove ${shortPluginName(n)}: ${(err as Error).message}`)
      }
    }
    return
  }

  const fullName = resolvePluginName(name!, discovered)
  const short = shortPluginName(fullName)

  if (!isPackageInstalled(fullName) && !getPluginState(fullName)) {
    consola.warn(`Plugin "${short}" is not installed.`)
    return
  }

  const confirmed = await consola.prompt(
    `Remove plugin "${short}"? This will uninstall the dependency.\n` +
    `  Tip: run "nexus migrate drop" from the plugin directory first to drop its tables.`,
    { type: 'confirm', initial: false }
  )

  if (!confirmed || typeof confirmed === 'symbol') {
    consola.info('Aborted.')
    return
  }

  try {
    consola.start(`Removing ${fullName}...`)
    await uninstallPlugin(fullName)
    consola.success(`Plugin ${short} removed.`)
  } catch (err) {
    consola.error(`Failed to remove plugin: ${(err as Error).message}`)
  }
}

// ── nexus plugin enable <name> ──────────────────────────────────────────────

export async function handlePluginEnable(name: string | undefined, options: { all?: boolean } = {}): Promise<void> {
  if (!name && !options.all) {
    consola.warn('Provide a plugin name or use -a to enable all.')
    return
  }
  if (name && options.all) {
    consola.warn('Cannot combine plugin name with -a flag.')
    return
  }

  initCli()
  const projectPath = getProjectPath()

  if (options.all) {
    const states = getAllPluginStates(projectPath)
    const disabled = Object.entries(states).filter(([, s]) => !s.enabled)

    if (disabled.length === 0) {
      consola.info('All plugins are already enabled.')
      return
    }

    for (const [n] of disabled) {
      enablePlugin(n, projectPath)
      consola.success(`${shortPluginName(n)} enabled.`)
    }
    consola.info('Restart server to apply.')
    return
  }

  const discovered = await discoverPlugins(projectPath)
  const fullName = resolvePluginName(name!, discovered)

  try {
    enablePlugin(fullName)
    consola.success(`Plugin ${shortPluginName(fullName)} enabled. Restart server to apply.`)
  } catch (err) {
    consola.error((err as Error).message)
  }
}

// ── nexus plugin disable <name> ─────────────────────────────────────────────

export async function handlePluginDisable(name: string | undefined, options: { all?: boolean } = {}): Promise<void> {
  if (!name && !options.all) {
    consola.warn('Provide a plugin name or use -a to disable all.')
    return
  }
  if (name && options.all) {
    consola.warn('Cannot combine plugin name with -a flag.')
    return
  }

  initCli()
  const projectPath = getProjectPath()

  if (options.all) {
    const states = getAllPluginStates(projectPath)
    const enabled = Object.entries(states).filter(([, s]) => s.enabled)

    if (enabled.length === 0) {
      consola.info('All plugins are already disabled.')
      return
    }

    for (const [n] of enabled) {
      disablePlugin(n, projectPath)
      consola.success(`${shortPluginName(n)} disabled.`)
    }
    consola.info('Restart server to apply.')
    return
  }

  const discovered = await discoverPlugins(projectPath)
  const fullName = resolvePluginName(name!, discovered)

  try {
    disablePlugin(fullName)
    consola.success(`Plugin ${shortPluginName(fullName)} disabled. Restart server to apply.`)
  } catch (err) {
    consola.error((err as Error).message)
  }
}

// ── nexus plugin info <name> ────────────────────────────────────────────────

export async function handlePluginInfo(name: string): Promise<void> {
  initCli()

  const projectPath = getProjectPath()
  const discovered = await discoverPlugins(projectPath)
  const fullName = resolvePluginName(name, discovered)
  const manifest = discovered.find(p => p.name === fullName)
  const state: PluginEntry | undefined = getPluginState(fullName, projectPath)

  if (!manifest && !state) {
    consola.warn(`Plugin "${shortPluginName(fullName)}" not found.`)
    return
  }

  console.log('')
  consola.info(`Plugin: ${fullName}`)

  if (manifest) {
    console.log(`  Code:        ${manifest.code}`)
    console.log(`  Version:     ${manifest.version}`)
    console.log(`  Category:    ${manifest.category}`)

    const desc = typeof manifest.description === 'string'
      ? manifest.description
      : (manifest.description as Record<string, string>)?.['en'] || Object.values(manifest.description || {})[0] || ''
    if (desc) console.log(`  Description: ${desc}`)

    console.log(`  Modules:     ${manifest.modules.map(m => m.name).join(', ')}`)
    console.log(`  Migrations:  ${manifest.migrationsDir ? 'yes' : 'no'}`)

    if (manifest.dependencies?.length) {
      console.log(`  Deps:        ${manifest.dependencies.join(', ')}`)
    }
    if (manifest.optionalDependencies?.length) {
      console.log(`  Opt deps:    ${manifest.optionalDependencies.join(', ')}`)
    }

    // Entities per module
    for (const mod of manifest.modules) {
      const entities = mod.definitions?.map(d => (d as { table?: string }).table).filter(Boolean) ?? []
      if (entities.length > 0) {
        console.log(`  ${mod.name} tables: ${entities.join(', ')}`)
      }
    }
  }

  if (state) {
    console.log('')
    console.log(`  State:`)
    console.log(`    Enabled:   ${state.enabled ? 'yes' : 'no'}`)
  } else {
    console.log('')
    console.log(`  Not registered in nexus.plugins.json`)
  }

  console.log('')
}
